import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Users,
  DollarSign,
  Clock,
  Heart,
  Stethoscope,
  Pill,
  Microscope,
  Shield,
  Zap,
  Target,
  BarChart3,
  PieChart,
  LineChart
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, ResponsiveContainer } from 'recharts';

import { LaboratoryData } from '../types/MedicalData';

interface TrendCardProps {
  data: LaboratoryData;
  onClick: () => void;
}

const iconMap: { [key: string]: React.ComponentType<any> } = {
  activity: Activity,
  trendingUp: TrendingUp,
  trendingDown: TrendingDown,
  users: Users,
  dollarSign: DollarSign,
  clock: Clock,
  heart: Heart,
  stethoscope: Stethoscope,
  pill: Pill,
  microscope: Microscope,
  shield: Shield,
  zap: Zap,
  target: Target,
  barChart: BarChart3,
  pieChart: PieChart,
  lineChart: LineChart
};

const TrendCard: React.FC<TrendCardProps> = ({ data, onClick }) => {
  const IconComponent = iconMap[data.icon] || Activity;
  
  const getCLIACardClass = (categoryType: string) => {
    switch (categoryType) {
      case 'Quality Assurance':
        return 'quality-assurance-card';
      case 'Diagnostic Performance':
        return 'diagnostic-performance-card';
      case 'Case Correlation':
        return 'case-correlation-card';
      case 'Compliance Monitoring':
        return 'compliance-monitoring-card';
      case 'Volume Tracking':
        return 'laboratory-card';
      default:
        return 'laboratory-card';
    }
  };

  const getComplianceStatus = (data: any) => {
    // Determine compliance status based on current value and thresholds
    if (data.unit === '%') {
      if (data.currentValue >= 95) return 'compliant';
      if (data.currentValue >= 90) return 'warning';
      return 'non-compliant';
    }
    if (data.unit === 'days') {
      if (data.currentValue <= 2) return 'compliant';
      if (data.currentValue <= 3) return 'warning';
      return 'non-compliant';
    }
    return 'compliant';
  };
  
  const getTrendIcon = () => {
    switch (data.trend) {
      case 'up':
        return <TrendingUp className="trend-icon up" />;
      case 'down':
        return <TrendingDown className="trend-icon down" />;
      default:
        return <Minus className="trend-icon stable" />;
    }
  };

  const formatValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  return (
    <motion.div 
      className={`trend-card ${data.priority} ${getCLIACardClass(data.cliaCategory.type)}`}
      onClick={onClick}
      whileHover={{ 
        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        y: -5
      }}
      transition={{ duration: 0.3 }}
    >
      {/* CLIA Compliance Indicator */}
      <div className={`clia-compliance-indicator ${getComplianceStatus(data)}`}></div>
      
      {/* CLIA Category Badge */}
      <div className="clia-category-badge">
        {data.cliaCategory.type}
      </div>
      <div className="card-header">
        <div className="icon-container" style={{ backgroundColor: data.color }}>
          <IconComponent size={24} />
        </div>
        <div className="trend-indicator">
          {getTrendIcon()}
          <span className={`trend-percentage ${data.trend}`}>
            {data.trendPercentage > 0 ? '+' : ''}{data.trendPercentage.toFixed(1)}%
          </span>
        </div>
      </div>

      <div className="card-content">
        <h3 className="card-title">{data.title}</h3>
        <p className="card-category">{data.category}</p>
        
        <div className="value-container">
          <span className="current-value">
            {formatValue(data.currentValue)}
            <span className="unit">{data.unit}</span>
          </span>
          <span className="previous-value">
            Previous: {formatValue(data.previousValue)}{data.unit}
          </span>
        </div>

        <p className="card-description">{data.description}</p>
      </div>

      {/* Mini Sparkline Chart */}
      <div className="sparkline-container">
        <div className="sparkline-header">
          <span className="sparkline-title">12-Month Trend</span>
          <span className="sparkline-subtitle">{data.cliaCategory.type}</span>
        </div>
        <div className="sparkline-chart">
          <ResponsiveContainer width="100%" height={60}>
            <RechartsLineChart data={data.monthlyData}>
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={data.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3, fill: data.color }}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
        <div className="sparkline-stats">
          <div className="stat-item">
            <span className="stat-label">Peak</span>
            <span className="stat-value">
              {Math.max(...data.monthlyData.map(d => d.value)).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg</span>
            <span className="stat-value">
              {Math.round(data.monthlyData.reduce((sum, d) => sum + d.value, 0) / data.monthlyData.length).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Growth</span>
            <span className={`stat-value ${data.trend}`}>
              {data.trendPercentage > 0 ? '+' : ''}{data.trendPercentage.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      <div className="card-footer">
        <div className="priority-badge">
          <span className={`priority ${data.priority}`}>
            {data.priority.toUpperCase()}
          </span>
        </div>
        <div className="click-indicator">
          <span>Click to view CLIA report</span>
        </div>
      </div>
    </motion.div>
  );
};

export default TrendCard;
